import { AlertCircleIcon } from "lucide-react"
import type { ReactNode } from "react"
import { Spinner } from "~/components/ui/spinner"
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from "~/components/ui/empty"
import { cn } from "~/lib/utils"

export function LoadingState({
	label,
	className,
	scope = "section",
	branded = false
}: {
	label?: string
	className?: string
	scope?: "page" | "section" | "inline"
	branded?: boolean
}) {
	return (
		<div
			className={cn(
				"flex w-full items-center justify-center",
				scope === "page" && "min-h-svh",
				scope === "section" && "h-full min-h-0 flex-1",
				scope === "inline" && "py-6",
				branded && "orbit-full-screen bg-orbit-gradient text-text-main",
				className
			)}
		>
			<Spinner
				aria-label={label ?? "Loading"}
				size={scope === "inline" ? "sm" : "lg"}
			/>
		</div>
	)
}

export function EmptyState({
	icon,
	title,
	description,
	className,
	actions,
	scope = "section"
}: {
	icon?: ReactNode
	title: string
	description?: ReactNode
	className?: string
	actions?: ReactNode
	scope?: "page" | "section" | "inline"
}) {
	return (
		<div
			className={cn(
				"flex w-full items-center justify-center",
				scope === "page" && "min-h-svh",
				scope === "section" && "h-full min-h-0 flex-1",
				className
			)}
		>
			<Empty>
				<EmptyHeader>
					{icon && <EmptyMedia>{icon}</EmptyMedia>}
					<EmptyTitle>{title}</EmptyTitle>
					{description && <EmptyDescription>{description}</EmptyDescription>}
				</EmptyHeader>
				{actions && <EmptyContent>{actions}</EmptyContent>}
			</Empty>
		</div>
	)
}

export function ErrorState({
	title = "Something went wrong.",
	description,
	className,
	actions
}: {
	title?: string
	description?: ReactNode
	className?: string
	actions?: ReactNode
}) {
	return (
		<EmptyState
			icon={<AlertCircleIcon className="size-6" />}
			title={title}
			description={description}
			className={className}
			actions={actions}
		/>
	)
}
